import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/Button';
import Modal from '../../../../components/Modal';
import { useRestoreClientSessionsMutation } from '../../../../../generated/graphql';
import { customToast } from '../../../../components/ToastAlert/customToast';
import { ClientSession } from '../../../../../generated/graphql';
import { logger } from '../../../../../lib/logger';
import { pluralize } from '../../../../lib/copy';
import { SESSION_STATES } from '../../utils/constants';

interface RestoreModalProps {
  sessions: ClientSession[];
  onClose: () => void;
  onSuccess: () => void;
}

const RestoreModal: React.FC<RestoreModalProps> = ({
  sessions,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [restoreSessions, { loading: isRestoringSessions }] =
    useRestoreClientSessionsMutation();

  const handleRestore = async () => {
    try {
      const { data } = await restoreSessions({
        variables: {
          ids: sessions.map((session) => session.id),
        },
      });

      if (data?.restoreClientSessions.success) {
        const summary = data.restoreClientSessions.summary;
        customToast.success(
          summary.processedCount === 1
            ? 'Session restored to pending'
            : `Successfully restored ${summary.processedCount} of ${
                summary.requestedCount
              } ${pluralize(
                summary.processedCount,
                'session',
                'sessions',
                false,
              )}.`,
        );
        onSuccess();
        onClose();
        navigate(SESSION_STATES.PENDING.path);
      } else {
        customToast.error(
          `Failed to restore ${pluralize(
            sessions.length,
            'session',
            'sessions',
            false,
          )}.`,
        );
        onClose();
      }
    } catch (error) {
      logger.error('Error restoring sessions:', error);
      customToast.error(
        `Failed to restore ${pluralize(
          sessions.length,
          'session',
          'sessions',
          false,
        )}.`,
      );
      onClose();
    }
  };

  return (
    <Modal
      name="ConfirmRestore"
      isOpen={true}
      setClosed={onClose}
      width="small"
    >
      <div className="px-10 py-8">
        <div className="flex flex-col items-center">
          <div className="mb-2 flex text-center font-serif text-subtitle-small text-green-150">
            Would you like to restore{' '}
            {sessions.length === 1
              ? 'this session'
              : `these ${sessions.length} sessions`}
            ?
          </div>
          <div className="text-center text-body text-green-125">
            {sessions.length === 1
              ? 'This session will be moved back to the pending.'
              : `These sessions will be moved back to the pending.`}
          </div>
        </div>
        <div className="mt-11 flex w-full flex-row items-center justify-center">
          <Button
            title="Cancel"
            theme="secondary"
            className="mr-5"
            onClick={onClose}
          />
          <Button
            title="Restore"
            theme="primary"
            onClick={handleRestore}
            disabled={isRestoringSessions}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RestoreModal;
